/**
 * roleLifecycle.js — single source of truth for the canonical
 * `active` / `live` alias on the client.
 *
 * Mirrors the doctrine declared in `backend/core/emp_role_lifecycle.py`
 * (`_STATUS_ALIASES`), which states that `'live'` IS the deprecated
 * read-alias of `'active'` — both values represent the SAME lifecycle
 * state. Any client-side counting/filter that ignores the alias
 * silently drops legacy `'live'` rows and underflows to 0, which
 * produced the RC 1.61 dashboard-zero incident (audit:
 * /app/memory/RC_1_61_EMPLOYER_DASHBOARD_DATA_INTEGRITY_AUDIT.md).
 *
 * Use `isActiveRoleStatus(status)` in every place that asks
 * "is this role currently active?". Adding a new status alias here
 * propagates everywhere automatically.
 *
 * Pin: `src/lib/operationController/tests/rc_1_61_bug_a_active_role_filter_pin.node.test.cjs`
 * guards both the helper itself and every callsite that imports it.
 */

/**
 * Canonical "active" lifecycle states for an employer role.
 *
 * @internal Exposed for the test pin only. Application code must use
 *           `isActiveRoleStatus(status)` instead of touching this set.
 */
export const ACTIVE_ROLE_STATUSES = Object.freeze(new Set(['active', 'live']));

/**
 * Returns `true` when the supplied DB status represents an active
 * employer role (i.e. publishable, billable, counter-tracked).
 *
 * Tolerates `null`, `undefined`, mixed case, and surrounding
 * whitespace — all map to `false` (no false positives) unless they
 * normalise to one of the canonical aliases.
 *
 * @param {string | null | undefined} status — raw `employer_roles.status`
 *        as returned by the API.
 * @returns {boolean}
 */
export function isActiveRoleStatus(status) {
  if (status == null) return false;
  return ACTIVE_ROLE_STATUSES.has(String(status).trim().toLowerCase());
}
